import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Policy } from '@/modules/Policy/interfaces/policy'
import { Statement, StatementLogs } from '@/modules/Statement/interfaces/statement'
import { SnackType } from '@/interfaces/snack'
import { showErrors, showNotification } from '@/utils/snack'
import { ApolloResponse } from '@/interfaces/apollo'
import { dateTimeToDisplay, dateToDisplay, monthToDisplay } from '@/utils/dateTime'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppTabs from '@/components/AppTabs.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppRadio from '@/components/AppRadio.vue'
import AcordFormDialog from '@/components/AcordFormDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import S3Handler from '@/mixins/s3'
import StatementDialog from '@/modules/Statement/scripts/statement-dialog'
import { getStatementById, bindPolicyAndStatement, getPoliciesByNameAndNumber } from '@/modules/Statement/gql/statement.gql'
import { statementTableHeaderName } from '@/modules/Statement/helpers/statement'
import { policyStatus } from '@/modules/Policy/helpers/policy'

@Component({
  components: {
    AppBreadcrumbs,
    AppTabs,
    AppDialog,
    AppTextSearch,
    AppCheckbox,
    AppRadio,
    AcordFormDialog,
    AppTextField
  }
})

export default class StatementDetail extends mixins(StatementDialog, S3Handler) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Accountings'
    },
    {
      text: 'Statement Upload',
      to: { name: 'StatementUploadList' }
    }
  ]

  statement: Statement | null = null

  dateTimeToDisplay = dateTimeToDisplay
  dateToDisplay = dateToDisplay
  monthToDisplay = monthToDisplay

  statements: any[] = []

  tableColumn: any[] = [
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, resizable: true },
    { field: 'status', title: 'Status', slots: { default: 'status_default' }, resizable: true },
    { field: 'relatedPolicy',
      title: 'Related Policy',
      slots: { default: 'policy_default' },
      resizable: true
    }
  ]

  tableLoading = false

  activeDelete = false
  activeDownload = false
  activeComplete = false

  scrollYConfig = {
    enabled: false
  }

  resultCheckedIndex = -1
  selectPolicyDialog = {
    show: false,
    title: 'Add Commission To'
  }

  policyList: Policy[] = []
  policySearchResults: Policy[] = []

  policyFilter = {
    number: '',
    name: ''
  }

  checkedStatementDetail: StatementLogs | null = null

  policyStatus = policyStatus

  btnLoading = false

  mounted () {
    this.getStatement()
  }

  @Watch('$route.params.id')
  onRouteParamUpdate (val: string) {
    this.getStatement()
  }

  async getStatement () {
    this.tableLoading = true
    const statementId = Number(this.$route.params.id)
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getStatementById,
        variables: {
          statementId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.statement = result.data.getStatementById as Statement
      this.statements = result.data.getStatementById.details

      this.$R.forEach((bindinglog: StatementLogs) => {
        bindinglog.tableData = JSON.parse(bindinglog.valuesStr)
        bindinglog.tableDataKeyList = this.$R.keys(bindinglog.tableData)
      }, this.statements)

      if (this.tableColumn.length >= 3) {
        this.tableColumn[0].width = 100
        this.tableColumn[1].width = 100
        this.tableColumn[2].width = 150
        if (this.tableColumn.length > 3) {
          this.tableColumn.splice(3, this.statements.length - 3)
        }
      }
      if (this.statements.length > 0) {
        this.statements[0].tableDataKeyList.sort((start: string, next: string) => {
          var order = ['commPercentage', 'grossPremium', 'tranDate', 'agentName', 'insuredName', 'policyNumber']
          return order.indexOf(next) - order.indexOf(start)
        })
        this.$R.forEach((key: string) => {
          const item = { field: key,
            title: statementTableHeaderName[key],
            slots: { default: ({ row }: any, h: any) => {
              return [
                h('span', row.tableData[key] || '-')
              ]
            }
            },
            resizable: true,
            width: key.length > 7 ? key.length > 15 ? key.length > 22 ? 320 : 250 : 220 : 120
          }
          this.tableColumn.push(item)
        }, this.statements[0].tableDataKeyList)
      }

      if (this.breadcrumbs.length === 2) {
        this.breadcrumbs.push({
          text: `ID: ${statementId}`
        })
      }
      this.tableLoading = false
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async deleteStatementConfirm () {
    await this.deleteStatementPost()
    this.$router.replace({
      name: 'StatementUploadList'
    })
  }

  relatePolicy (item: any) {
    this.checkedStatementDetail = item
    this.selectPolicyDialog.show = true
  }

  async relatePolicyConfirm () {
    try {
      const result = await this.$apollo.mutate({
        mutation: bindPolicyAndStatement,
        variables: {
          statementDetailId: this.checkedStatementDetail ? Number(this.checkedStatementDetail.id) : null,
          policyId: this.resultCheckedIndex !== -1 ? Number(this.policySearchResults[this.resultCheckedIndex].id) : null
        }
      })

      const res = result.data.bindPolicyAndStatement
      if (res) {
        showNotification({
          msg: 'Bind Policy successfully',
          type: SnackType.Success
        })
        this.selectPolicyDialog.show = false
        this.clearPolicyFilter()
        this.getStatement()
      }
    } catch (error) {
      showErrors(error.graphQLErrors)
    }
  }

  hidePolicyDialog () {
    this.selectPolicyDialog.show = false
    this.clearPolicyFilter()
  }

  async getPoliciesByNameAndNumber (val: any) {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPoliciesByNameAndNumber,
        variables: {
          policyName: val.name ? val.name : '',
          policyNumber: val.type !== 'query' ? this.policyFilter.number : val.number ? val.number : ''
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (val.type === 'query') {
        this.policyList = result.data.getPoliciesByNameAndNumber
      } else {
        this.resultCheckedIndex = -1
        this.policySearchResults = result.data.getPoliciesByNameAndNumber
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  clearPolicyFilter () {
    this.resultCheckedIndex = -1
    this.policyList = []
    this.policySearchResults = []
    this.policyFilter = {
      number: '',
      name: ''
    }
  }

  async downloadFileHandler (file: any) {
    this.btnLoading = true
    await this.downloadFile(file)
    this.btnLoading = false
  }
}
